import DashboardLayout from "../../../lib/components/layouts/dashboard";
import { FC, useContext } from "react";
import { AppContext } from "lib/contexts/app-context";
import { isPatientUser } from "lib/utils/util";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { useNavigate, useLocation } from "react-router-dom";
import SimpleCard from "lib/components/common/simple-card";
import { Row, Col } from "react-bootstrap";
import SimpleButton from "lib/components/common/simple-button";

interface PatientCheckoutSuccessPageProps { }

const PatientCheckoutSuccessPage: FC<PatientCheckoutSuccessPageProps> = ({ }) => {
  const { appState } = useContext(AppContext);
  const navigateTo = useNavigate();
  const location = useLocation();

  // Get booking details from navigation state
  const bookingDetails = location.state?.bookingDetails || location.state?.originalBookingDetails;

  // Debug: Log the location state to see what's being passed
  console.log('Location state:', location.state);
  console.log('Booking details:', bookingDetails);

  const BreadcrumbItems = [
    {
      label: "Dashboard",
      path: isPatientUser(appState?.userRole)
        ? PAGE_ROUTES.PATIENT.Dashboard
        : PAGE_ROUTES.PRACTITIONER.Dashboard,
    },
    {
      label: "Booking Success",
      path: PAGE_ROUTES.PATIENT.BookingSuccess,
    },
  ] as BreadcrumbItem[];

  const handleGoToDashboard = () => {
    navigateTo(PAGE_ROUTES.PATIENT.Dashboard);
  };

  const handleBookAnother = () => {
    navigateTo(PAGE_ROUTES.PATIENT.BookAppointment);
  };

  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return null;
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const appointmentDateTime = bookingDetails?.fromTime ? formatDateTime(bookingDetails.fromTime) : null;

  return (
    <DashboardLayout breadcrumbItems={BreadcrumbItems}>
      <SimpleCard>
        <Row className="text-center">
          <Col>
            <div className="mb-4">
              <i className="fas fa-check-circle text-success" style={{ fontSize: "4rem" }}></i>
            </div>
            <h2 className="text-success mb-3 success-text">Appointment Booked Successfully!</h2>
            <p className="text-muted mb-4 ">
              Your appointment has been confirmed. You will receive a confirmation email shortly.
            </p>

            {bookingDetails ? (
              <div className="card bg-light mb-2 mb-md-4">
                <div className="card-body">
                  <h5 className="card-title mb-3 mb-md-0">Appointment Details</h5>
                  <div className="row text-start">
                    <div className="col-md-6 details-box">
                      <p><strong>Date:</strong> {appointmentDateTime?.date || 'Not available'}</p>
                      <p><strong>Time:</strong> {appointmentDateTime?.time || 'Not available'}</p>
                      <p><strong>Duration:</strong> {bookingDetails.duration} minutes</p>
                    </div>
                    <div className="col-md-6 details-box">
                      <p><strong>Practitioner:</strong> {bookingDetails.practitionerName}</p>
                      <p><strong>Therapy:</strong> {bookingDetails.therapyName}</p>
                      <p><strong>Clinic:</strong> {bookingDetails.clinicName}</p>
                    </div>
                  </div>
                  {bookingDetails.totalCost && (
                    <p className="mt-2"><strong>Cost:</strong> £{bookingDetails.totalCost}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="alert alert-info mb-4">
                <h5>Appointment Confirmed</h5>
                <p>Your appointment has been successfully booked. Please check your email for confirmation details.</p>
                <p className="mb-0"><small>If you don't receive an email within a few minutes, please contact support.</small></p>
              </div>
            )}

            <div className="justify-content-center">
              <SimpleButton
                onClick={handleGoToDashboard}
                variant="outline-primary"
                size="lg"
                className="fw-semibold fs-6 fs-sm-6 fs-md-5"
              >
                Go to Dashboard
              </SimpleButton>
              <SimpleButton
                onClick={handleBookAnother}
                variant="primary"
                size="lg"
                className="fw-semibold fs-6 fs-sm-6 fs-md-5"
              >
                Book Another Appointment
              </SimpleButton>
            </div>
          </Col>
        </Row>
      </SimpleCard>
    </DashboardLayout>
  );
};

export default PatientCheckoutSuccessPage;
